import { Timestamp } from 'firebase/firestore';
import {
  TechnicalInspectionRule,
  TechnicalInspectionInfo,
  Vehicle,
  VehicleType,
  TechnicalDetails,
  FuelType
} from '../types/vehicle';

interface VehicleDetails {
  manufacturerDetails?: {
    make?: string;
    model?: string;
    year?: number;
  };
  enginePowerPS?: number;
  engineSize?: number;
  engineTorque?: number;
  co2Emissions?: number;
  ptac?: number;
}

interface VehicleSearchCriteria {
  licensePlate: string;
  make: string;
  model: string;
  year: number;
}

// Règles par défaut pour la France
const defaultFrenchRules: TechnicalInspectionRule[] = [
  {
    countryCode: 'FR',
    vehicleType: 'PERSONAL_VEHICLE',
    firstInspectionMonths: 48,    // 4 ans
    regularInspectionMonths: 24,  // 2 ans
    inspectionWindowMonths: 2,    // 2 mois avant échéance
    counterVisitMonths: 2
  },
  {
    countryCode: 'FR',
    vehicleType: 'SUV_4X4',
    firstInspectionMonths: 48,    // 4 ans
    regularInspectionMonths: 24,  // 2 ans
    inspectionWindowMonths: 2,
    counterVisitMonths: 2
  },
  {
    countryCode: 'FR',
    vehicleType: 'GPL_VEHICLE',
    firstInspectionMonths: 48,    // 4 ans
    regularInspectionMonths: 24,  // 2 ans
    inspectionWindowMonths: 2,
    counterVisitMonths: 2
  },
  {
    countryCode: 'FR',
    vehicleType: 'ELECTRIC_HYBRID',
    firstInspectionMonths: 48,    // 4 ans
    regularInspectionMonths: 24,  // 2 ans
    inspectionWindowMonths: 2,
    counterVisitMonths: 2
  },
  {
    countryCode: 'FR',
    vehicleType: 'LIGHT_COMMERCIAL',
    firstInspectionMonths: 48,
    regularInspectionMonths: 24,
    inspectionWindowMonths: 2,
    counterVisitMonths: 2,
    pollutionCheck: {
      firstCheckMonths: 48,
      regularCheckMonths: 12,
      checkWindowStart: 10,
      checkWindowEnd: 12,
      counterVisitMonths: 2,
      applicability: {
        maxPTAC: 3.5,
        minYearPetrol: 1972,
        minYearDiesel: 1980
      }
    }
  },
  {
    countryCode: 'FR',
    vehicleType: 'CAMPING_CAR',
    firstInspectionMonths: 48,    // 4 ans
    regularInspectionMonths: 24,  // 2 ans
    inspectionWindowMonths: 2,
    counterVisitMonths: 2
  },
  {
    countryCode: 'FR',
    vehicleType: 'COLLECTION',
    firstInspectionMonths: 48,
    regularInspectionMonths: 60,  // 5 ans
    inspectionWindowMonths: 2,
    counterVisitMonths: 2
  },
  {
    countryCode: 'FR',
    vehicleType: 'MOTORCYCLE',
    firstInspectionMonths: 60,    // 5 ans
    regularInspectionMonths: 36,  // 3 ans
    inspectionWindowMonths: 6,    // 6 mois avant échéance
    counterVisitMonths: 2
  },
  {
    countryCode: 'FR',
    vehicleType: 'TAXI_VTC',
    firstInspectionMonths: 12,
    regularInspectionMonths: 12,  // Annuel
    inspectionWindowMonths: 1,
    counterVisitMonths: 2,
    specialConditions: {
      condition: 'TAXI',
      overrideMonths: 12
    }
  },
  {
    countryCode: 'FR',
    vehicleType: 'MEDICAL_TRANSPORT',
    firstInspectionMonths: 12,    // 1 an
    regularInspectionMonths: 12,  // Annuel
    inspectionWindowMonths: 1,
    counterVisitMonths: 2
  },
  {
    countryCode: 'FR',
    vehicleType: 'BREAKDOWN_VEHICLE',
    firstInspectionMonths: 12,    // 1 an
    regularInspectionMonths: 12,  // Annuel
    inspectionWindowMonths: 1,
    counterVisitMonths: 2
  }
];

export class VehicleTechnicalService {
  private rules: TechnicalInspectionRule[];

  constructor() {
    this.rules = [...defaultFrenchRules];
  }

  static async getVehicleDetailsByLicensePlate(licensePlate: string): Promise<VehicleDetails | null> {
    try {
      // TODO: Implémenter l'appel à l'API pour récupérer les détails
      return {
        manufacturerDetails: {
          make: "Marque exemple",
          model: "Modèle exemple",
          year: 2020
        },
        enginePowerPS: 150,
        engineSize: 2000,
        engineTorque: 320,
        co2Emissions: 120
      };
    } catch (error) {
      console.error("Erreur lors de la récupération des détails du véhicule:", error);
      return null;
    }
  }

  static async refreshTechnicalDetails(criteria: VehicleSearchCriteria): Promise<TechnicalDetails | null> {
    try {
      return {
        manufacturerDetails: {
          make: criteria.make,
          model: criteria.model,
          year: criteria.year
        },
        enginePowerPS: 150,
        enginePowerKW: 110,
        engineSize: 2000,
        engineTorque: 320,
        fuelCapacity: 60,
        weightKg: 1500,
        wheelbase: 2700,
        length: 4500,
        width: 1800,
        height: 1600,
        co2Emissions: 120,
        euroEmissionsClass: "Euro 6",
        ptac: 2000
      };
    } catch (error) {
      console.error("Erreur lors du rafraîchissement des détails techniques:", error);
      return null;
    }
  }

  getRules(countryCode: string, vehicleType: VehicleType): TechnicalInspectionRule | null {
    return this.rules.find(rule => 
      rule.countryCode === countryCode && rule.vehicleType === vehicleType
    ) || null;
  }

  initializeTechnicalInspection(
    firstRegistrationDate: Timestamp, 
    vehicleType: VehicleType, 
    fuelType: FuelType,
    year: number,
    technicalDetails: TechnicalDetails | undefined,
    countryCode: string = 'FR'
  ): TechnicalInspectionInfo {
    const rules = this.getRules(countryCode, vehicleType);
    if (!rules) {
      throw new Error(`No rules found for vehicle type ${vehicleType} in country ${countryCode}`);
    }

    const firstRegistrationDateJs = firstRegistrationDate.toDate();
    const firstInspectionDate = new Date(firstRegistrationDateJs);
    firstInspectionDate.setMonth(firstInspectionDate.getMonth() + rules.firstInspectionMonths);

    // Créer un objet TechnicalInspectionInfo complet avec toutes les propriétés requises
    // Créer un objet Vehicle temporaire pour vérifier si le contrôle antipollution est requis
    const vehicleData = {
      vehicleType,
      firstRegistrationDate,
      fuelType,
      technicalDetails,
      year
    } as Vehicle;

    const isPollutionCheckRequired = this.isPollutionCheckRequired(vehicleData, rules);

    const technicalInspection: TechnicalInspectionInfo = {
      lastInspectionDate: firstRegistrationDate,
      nextInspectionDate: Timestamp.fromDate(firstInspectionDate),
      status: 'pending',
      documents: [],
      history: []
    };

    if (isPollutionCheckRequired) {
      // Only set pollution check dates if required
      technicalInspection.lastPollutionCheckDate = firstRegistrationDate;
      const pollutionCheckDate = new Date(firstRegistrationDateJs);
      pollutionCheckDate.setMonth(pollutionCheckDate.getMonth() + rules.pollutionCheck!.firstCheckMonths);
      technicalInspection.nextPollutionCheckDate = Timestamp.fromDate(pollutionCheckDate);
    }

    return technicalInspection;
  }

  calculateNextInspectionDate(
    vehicle: Vehicle,
    countryCode: string = 'FR'
  ): { nextDate: Date; isPollutionCheckRequired: boolean } {
    const rules = this.getRules(countryCode, vehicle.vehicleType);
    if (!rules) {
      throw new Error(`No rules found for vehicle type ${vehicle.vehicleType} in country ${countryCode}`);
    }

    const firstRegistration = vehicle.firstRegistrationDate.toDate();
    const lastInspection = vehicle.technicalInspection.lastInspectionDate?.toDate();
    const today = new Date();

    // Si pas encore de premier contrôle
    if (!lastInspection) {
      const firstInspectionDate = new Date(firstRegistration);
      firstInspectionDate.setMonth(firstInspectionDate.getMonth() + rules.firstInspectionMonths);
      return {
        nextDate: firstInspectionDate,
        isPollutionCheckRequired: this.isPollutionCheckRequired(vehicle, rules)
      };
    }

    // Calcul du prochain contrôle régulier
    const nextDate = new Date(lastInspection);
    nextDate.setMonth(nextDate.getMonth() + rules.regularInspectionMonths);

    return {
      nextDate,
      isPollutionCheckRequired: this.isPollutionCheckRequired(vehicle, rules)
    };
  }

  isPollutionCheckRequired(vehicle: Vehicle, rules: TechnicalInspectionRule): boolean {
    if (!rules.pollutionCheck) return false;

    const { pollutionCheck } = rules;
    const vehicleYear = vehicle.year;
    const ptac = vehicle.technicalDetails?.ptac || 0;

    if (ptac > pollutionCheck.applicability.maxPTAC) return false;
    
    if (vehicle.fuelType === 'gasoline' && 
        vehicleYear < pollutionCheck.applicability.minYearPetrol) {
      return false;
    }
    
    if (vehicle.fuelType === 'diesel' && 
        vehicleYear < pollutionCheck.applicability.minYearDiesel) {
      return false;
    }

    if (['electric', 'hybrid'].includes(vehicle.fuelType)) return false;

    return true;
  }

  calculateNextPollutionCheckDate(
    vehicle: Vehicle,
    countryCode: string = 'FR'
  ): Date | null {
    const rules = this.getRules(countryCode, vehicle.vehicleType);
    if (!rules?.pollutionCheck) return null;

    const lastCheck = vehicle.technicalInspection.lastPollutionCheckDate?.toDate();
    if (!lastCheck) {
      const firstRegistration = vehicle.firstRegistrationDate.toDate();
      const firstCheckDate = new Date(firstRegistration);
      firstCheckDate.setMonth(firstCheckDate.getMonth() + rules.pollutionCheck.firstCheckMonths);
      return firstCheckDate;
    }

    const nextDate = new Date(lastCheck);
    nextDate.setMonth(nextDate.getMonth() + rules.pollutionCheck.regularCheckMonths);
    return nextDate;
  }

  isCounterVisitRequired(vehicle: Vehicle): boolean {
    const { technicalInspection } = vehicle;
    if (!technicalInspection?.counterVisitDueDate) return false;

    try {
      const dueDate = technicalInspection.counterVisitDueDate.toDate();
      const today = new Date();
      return dueDate > today;
    } catch (error) {
      console.error('Error checking counter visit:', error);
      return false;
    }
  }

  calculateInspectionStatus(vehicle: Vehicle): 'valid' | 'pending' | 'expired' | 'failed' {
    const { technicalInspection } = vehicle;
    const today = new Date();

    // Si contre-visite requise
    if (this.isCounterVisitRequired(vehicle)) {
      return 'failed';
    }

    // Si pas encore de contrôle effectué
    if (!technicalInspection.lastInspectionDate) {
      const { nextDate } = this.calculateNextInspectionDate(vehicle);
      return today > nextDate ? 'expired' : 'pending';
    }

    const nextDate = technicalInspection.nextInspectionDate.toDate();
    const warningDate = new Date(nextDate);
    warningDate.setMonth(warningDate.getMonth() - 2); // Alerte 2 mois avant

    if (today > nextDate) {
      return 'expired';
    } else if (today > warningDate) {
      return 'pending';
    }

    return 'valid';
  }
}

export default new VehicleTechnicalService();
